'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { usePageContext } from '@marketing-web/ui/composables/Analytics/PageContext';
import { pushPageView } from '@marketing-web/ui/composables/Analytics/PageViewHelper';

/**
 * PageViewTracker - Manually sends ALL page_view even
 *
 * How it works:
 * 1. Listens to Next.js route changes via usePathname
 * 2. Gets page_template and locale from PageContextProvider via usePageContext
 * 3. Manually sends page_view event to GA4 with correct page_template
 *
 * IMPORTANT:
 * - This must be placed INSIDE PageContextProvider
 * - Enhanced Measurement "Page views" must be DISABLED in GA4
 */
export const PageViewTracker = () => {
  const pathname = usePathname();
  const { pageTemplate, locale } = usePageContext();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Small delay to ensure:
    // 1. PageContextProvider's useEffect has run
    // 2. Document title is updated
    // 3. GA4 tag is loaded
    const timeout = setTimeout(() => {
      if (typeof window === 'undefined') return;

      pushPageView({
        pageTemplate,
        locale,
        pageLocation: window.location.href,
        pageTitle: document.title,
        pathname: pathname,
        isFirstLoad: isFirstRender.current,
      });

      isFirstRender.current = false;
    }, 100); // 100ms delay for first load, ensures everything is ready

    return () => clearTimeout(timeout);
  }, [pathname, pageTemplate, locale]);

  return null; // No UI
};
